import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { Box, Chip, Grid, IconButton, styled, Tooltip, Typography } from '@mui/material';
import React, { useState } from 'react';

import { highlightKeys } from '../../helper/KeywordHighlighter';
import WsColors from '../../WsColors';
import { WsButton } from '../WsButton';
import { WsTypoColors, WsTypoVariants } from '../WsTypography';

export enum SmartChipsType {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
}
const mapColor = new Map();
mapColor.set('light', WsColors.TheBlues100);
mapColor.set('secondary', WsColors.TertiaryShades100);
export interface WsSmartTagsListProps {
  chipArray: string[];
  count: number;
  type?: SmartChipsType;
  onDelete?: (chip: string) => void;
  allowDelete?: boolean;
  query?: string;
  borderColorFlag?: boolean;
  tagOnClick?: () => void;
}
export interface SmartTagType {
  type: string;
  allowDelete: boolean;
}
const ChipStyle = styled(Chip)(({ allowDelete }: SmartTagType) => ({
  borderRadius: 4,
  borderColor: WsColors.TertiaryShades100,
  backgroundColor: WsColors.TertiaryShades50,
  marginLeft: 4,
  marginTop: 4,
  paddingRight: allowDelete ? 0 : 8,
  paddingLeft: 3,
  userSelect: 'unset',
  ':hover': {
    borderColor: WsColors.TertiaryShades200,
    backgroundColor: `${WsColors.TertiaryShades100} !important`,
  },
}));

const GetTagName = (tag: string) => {
  enum TagValues {
    CLIENT_NAMES = 'Client Names',
    CLIENT_RECRUITERS = 'Client Recruiters',
    JOB_TITLE = 'Job Title',
    JOB_STATUS = 'Job Status',
    SALARY = 'Salary',
    JOB_LOCATIONS = 'Job Locations',
    CANDIDATE_LOCATIONS = 'Candidate Locations',
    JOB_TYPE = 'Job Type',
    SUBMISSION_X_HOURS_JOB_POSTING = 'Submission between X hours job posting',
    SUBMITTED_TEAM_MEMBER_ASSIGNED_JOB = 'Submitted Team Member Assigned Job',
    SUBMITTED_TEAM_ASSIGNED_JOB = 'Submitted Team Assigned Job',
    SUBMITTED_TO_JOB_POSTED_TEAM_MEMBER = 'Submitted to Job Posted Team Member',
    SUBMITTED_TO_JOB_POSTED_TEAM = 'Submitted to Job Posted Team',
    BUSINESS_UNIT = 'Business Unit',
    TAGS = 'Tags',
    RELOCATION_PREFERENCE = 'Relocation preference',
    STAGE_STATUS_BETWEEN = 'Stage Status Between',
    APPLICATION_STATUS = 'Application Status',
    APPLICATION_STAGE = 'Application Stage',
    stageDateFromDate = 'Stage Date From',
    stageDateToDate = 'Stage date To',
  }
  const value = TagValues[tag as keyof typeof TagValues];
  return value !== undefined ? value : tag;
};

const WsSmartTagList = ({
  chipArray,
  count,
  type = SmartChipsType.PRIMARY,
  onDelete = () => {},
  allowDelete = false,
  query,
  borderColorFlag = true,
  tagOnClick = () => {},
}: WsSmartTagsListProps) => {
  const [displayListLimit, setDisplayLimit] = useState(count);
  const [openIndex, setOpenIndex] = useState<number>();

  return (
    <Box>
      {chipArray.length > 0 && (
        <Grid container item xs={12}>
          {chipArray.slice(0, displayListLimit).map((chipName: string, index: number) => (
            <>
              <Tooltip
                open={openIndex === index}
                onClose={() => setOpenIndex(-1)}
                onOpen={() => setOpenIndex(index)}
                title={`${chipName.split(':')[1].trim()}`}
                arrow
              >
                <ChipStyle
                  key={index}
                  type={type}
                  size="small"
                  onDelete={onDelete ? () => onDelete(chipName) : undefined}
                  deleteIcon={
                    allowDelete ? (
                      <IconButton
                        sx={{
                          '&:hover': {
                            backgroundColor: 'transparent',
                          },
                        }}
                      >
                        <ClearOutlinedIcon
                          sx={{
                            height: '20px',
                            width: '20px',
                            color: WsColors.TertiaryShades700,
                          }}
                        />
                      </IconButton>
                    ) : (
                      <></>
                    )
                  }
                  label={
                    <>
                      <Box my={2} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          color={WsTypoColors.BLACK}
                          variant={WsTypoVariants.BODY2}
                          sx={{
                            cursor: 'pointer',
                            fontWeight: 500,
                            fontSize: '14px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '150px',
                          }}
                          dangerouslySetInnerHTML={{
                            __html: query
                              ? highlightKeys(chipName, query ?? '')
                              : `${GetTagName(chipName.split(':')[0].trim())}`,
                          }}
                        />
                        <Typography
                          color={WsTypoColors.BLACK}
                          variant={WsTypoVariants.BODY2}
                          sx={{
                            cursor: 'pointer',
                            fontWeight: 500,
                            fontSize: '14px',
                          }}
                        >
                          :
                        </Typography>
                        <Typography
                          color={WsTypoColors.PRIMARY}
                          variant={WsTypoVariants.BODY2}
                          sx={{
                            cursor: 'pointer',
                            fontWeight: 500,
                            fontSize: '14px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '150px',
                          }}
                          dangerouslySetInnerHTML={{
                            __html: query
                              ? highlightKeys(chipName, query ?? '')
                              : `${chipName.split(':')[1].trim()}`,
                          }}
                        />
                      </Box>
                    </>
                  }
                  allowDelete={allowDelete}
                  variant={borderColorFlag ? 'outlined' : undefined}
                  sx={{
                    borderWidth: borderColorFlag ? 1 : undefined,
                    borderStyle: borderColorFlag ? 'solid' : undefined,
                    maxWidth: '300px',
                  }}
                  onClick={() => borderColorFlag && tagOnClick()}
                />
              </Tooltip>
            </>
          ))}
          {chipArray.length > count && (
            <WsButton
              variant="text"
              size="medium"
              onClick={() =>
                setDisplayLimit(displayListLimit == chipArray.length ? count : chipArray.length)
              }
            >
              {displayListLimit < chipArray.length
                ? `+ ${chipArray.length - displayListLimit}more`
                : 'show less'}
            </WsButton>
          )}
        </Grid>
      )}
    </Box>
  );
};
export default WsSmartTagList;
